import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { actionErrorMessage } from '@/utils/actionErrorMessage';

import {
  hasSavedMessage,
  isBusyChat,
  isRefusedSend,
  lastSeenReplyId,
  withoutPendingTurn,
} from './chatMessages';
import { chatQueryKeys } from './queryKeys';
import type { ChatApi } from './types';
import { useSendChatMessageMutation } from './useSendChatMessageMutation';

/** A message that wasn't sent: it goes back in the box, with why. */
export interface ChatSendFailure {
  text: string;
  message: string;
  /** When it failed, so the same message failing twice still refills the box. */
  at: number;
}

const BUSY_MESSAGE =
  'Finlay is still answering in another tab. Send this again once that reply is in.';

/** Sends messages in an open chat, and puts back the ones that didn't reach finlay-api. */
export const useChatSend = (chat: ChatApi) => {
  const queryClient = useQueryClient();
  const sendMutation = useSendChatMessageMutation();
  const [failure, setFailure] = useState<ChatSendFailure | null>(null);

  const send = (text: string) => {
    if (sendMutation.isPending) return;
    setFailure(null);
    const queryKey = chatQueryKeys.detail(chat.id);
    const countBefore = chat.messages.length;

    sendMutation.mutate(
      {
        chatId: chat.id,
        text,
        lastSeenReplyId: lastSeenReplyId(chat.messages),
        sentAt: new Date().toISOString(),
      },
      {
        onError: async (error) => {
          if (isBusyChat(error)) {
            setFailure({ text, message: BUSY_MESSAGE, at: Date.now() });
            return;
          }
          if (!isRefusedSend(error)) {
            // The connection broke, perhaps after the message was saved: only a message finlay-api didn't save is
            // put back, so it can't be sent twice.
            await queryClient.refetchQueries({ queryKey });
            if (
              hasSavedMessage(
                queryClient.getQueryData(queryKey),
                text,
                countBefore,
              )
            ) {
              return;
            }
            queryClient.setQueryData<ChatApi>(
              queryKey,
              (current) => current && withoutPendingTurn(current),
            );
          }
          setFailure({
            text,
            message: actionErrorMessage('send your message', error),
            at: Date.now(),
          });
        },
      },
    );
  };

  return {
    send,
    sending: sendMutation.isPending,
    /** The message on its way, while it is sent and answered. */
    sendingText: sendMutation.isPending
      ? sendMutation.variables.text
      : undefined,
    failure,
  };
};
